import { formatPrice } from '@lichtblick/localization/helpers/numbers';
import { AccentText } from '@lichtblick/photon/atoms/accent-text/accent-text';
import { Text } from '@lichtblick/photon/atoms/text/text';
import type { FC, PropsWithChildren } from 'react';
import { getCurrentMonth } from '@/helpers/date';
import type { TariffsQuery } from '@/types/generated';
import { getLoyaltyBonusInEur } from '../card/card-utils';
import styles from './monthly-prices.module.css';

interface Props {
  price: string;
  headline: string;
  text: string;
}

const MonthlyPrice: FC<Props> = ({ price, headline, text }) => (
  <div className={styles['price-wrapper']}>
    <div className={styles.text}>
      <Text size="l" bold>
        {headline}
      </Text>
      <Text size="s">{text}</Text>
    </div>
    <AccentText className={styles.price}>{price}</AccentText>
  </div>
);

const MonthlyPricesWrapper: FC<PropsWithChildren> = ({ children }) => <div className={styles.wrapper}>{children}</div>;

export const MonthlyPrices: FC<{ product: TariffsQuery['tariffs'][number] }> = ({ product }) => {
  if (product.productCode?.includes('VARIO')) {
    return (
      <MonthlyPricesWrapper>
        <MonthlyPrice
          price={formatPrice(product.monthlyRate, '€', 0)}
          headline={`Dein Abschlag für den ${getCurrentMonth()}¹`}
          text="Der mtl. Abschlag zeigt, was du jeden Monat zahlst. Er kann variieren und richtet sich nach deinem Verbrauch und aktuellem Arbeitspreis."
        />
      </MonthlyPricesWrapper>
    );
  }

  if (product.productCode?.includes('DYNAMIC')) {
    return (
      <MonthlyPricesWrapper>
        <MonthlyPrice
          price={formatPrice(product.monthlyRate, '€', 0)}
          headline="Voraussichtliche Kosten pro Monat"
          text="Die voraussichtlichen Kosten pro Monat sind eine Prognose basierend auf aktuellen Börsenpreisen. Die tatsächlichen Kosten werden nach Monatsende anhand deines realen Verbrauchs und den dann gültigen Preisen abgerechnet."
        />
      </MonthlyPricesWrapper>
    );
  }

  const loyaltyBonusInEur = getLoyaltyBonusInEur(product);
  const instantBonusInEur = product?.bonus?.instantBonusInEur;
  const showBonus = Boolean(loyaltyBonusInEur ?? instantBonusInEur);

  return (
    <MonthlyPricesWrapper>
      {showBonus && (
        <MonthlyPrice
          price={formatPrice(product.monthlyPriceWithBonus, '€', 0)}
          headline="ø Kosten pro Monat"
          text="Die Auflistung zeigt, was du im ersten Jahr effektiv zahlst, abzüglich Boni und Rabatten."
        />
      )}
      <MonthlyPrice
        price={formatPrice(product.monthlyRate, '€', 0)}
        headline="Dein monatlicher Abschlag"
        text="Die Auflistung zeigt deinen monatlichen Zahlungsbetrag auf Basis deines Verbrauchs."
      />
    </MonthlyPricesWrapper>
  );
};
